import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_URL = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            window.location.href = '/connexion';
        }
        return Promise.reject(error);
    }
);

// Auth API
export const authAPI = {
    register: (data) => api.post('/auth/register', data),
    login: (data) => api.post('/auth/login', data),
    getMe: () => api.get('/auth/me'),
};

// Orders API
export const ordersAPI = {
    getAll: (params) => api.get('/orders', { params }),
    getOne: (id) => api.get(`/orders/${id}`),
    create: (data) => api.post('/orders', data),
    update: (id, data) => api.put(`/orders/${id}`, data),
    delete: (id) => api.delete(`/orders/${id}`),
    toggleReadyForPickup: (id) => api.post(`/orders/${id}/ready-for-pickup`),
    claim: (id) => api.post(`/orders/${id}/claim`),
    reject: (id, data) => api.post(`/orders/${id}/reject`, data),
    claimForVendor: (orderId, vendorId) => api.post(`/orders/${orderId}/claim-for-vendor?vendor_id=${vendorId}`),
    importFromFile: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/import/orders', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// Dashboard API
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
    getChartData: () => api.get('/dashboard/chart-data'),
};

// Activity Logs API
export const activityAPI = {
    getLogs: (limit = 50) => api.get('/activity-logs', { params: { limit } }),
};

// Shopify API
export const shopifyAPI = {
    getStatus: () => api.get('/shopify/status'),
    getProducts: (params) => api.get('/shopify/products', { params }),
    sync: () => api.post('/shopify/sync'),
};

// Abandoned Orders API
export const abandonedAPI = {
    getAll: (params) => api.get('/abandoned', { params }),
    getOne: (id) => api.get(`/abandoned/${id}`),
    create: (data) => api.post('/abandoned', data),
    update: (id, data) => api.put(`/abandoned/${id}`, data),
    delete: (id) => api.delete(`/abandoned/${id}`),
    importFromFile: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/import/abandoned', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// Clients API
export const clientsAPI = {
    getAll: (params) => api.get('/clients', { params }),
    getOne: (phone) => api.get(`/clients/${encodeURIComponent(phone)}`),
    update: (phone, data) => api.put(`/clients/${encodeURIComponent(phone)}`, data),
    checkStatus: (phone) => api.get(`/clients/check/${encodeURIComponent(phone)}`),
};

// Vendeurs & Drivers API
export const vendeursAPI = {
    getAll: () => api.get('/vendeurs'),
};

export const driversAPI = {
    getAll: () => api.get('/drivers'),
};

// Reassignment Chain Settings API
export const reassignmentChainAPI = {
    getSettings: () => api.get('/settings/reassignment-chain'),
    updateSettings: (data) => api.put('/settings/reassignment-chain', data),
    getAvailableVendors: () => api.get('/settings/available-vendors'),
};

// All Users API (for Livré par dropdown)
export const usersListAPI = {
    getAll: () => api.get('/users/list'),
};

// Organizations API (Super Admin)
export const organizationsAPI = {
    getAll: (niche) => api.get('/organizations', { params: niche ? { niche } : {} }),
    getOne: (id) => api.get(`/organizations/${id}`),
    create: (data) => api.post('/organizations', data),
    update: (id, data) => api.put(`/organizations/${id}`, data),
    updateSubscription: (id, data) => api.post(`/organizations/${id}/subscription`, data),
    createAdmin: (id, data) => api.post(`/organizations/${id}/admin`, data),
};

// Business Niches API
export const nichesAPI = {
    getAll: () => api.get('/niches'),
};

// Platform Stats API (Super Admin)
export const platformAPI = {
    getStats: () => api.get('/platform/stats'),
    getStatsByNiche: () => api.get('/platform/stats-by-niche'),
};

// Commission Reports API
export const reportsAPI = {
    getCommissions: (params) => api.get('/reports/commissions', { params }),
    getVendors: () => api.get('/reports/vendors'),
    addFee: (orderId, data) => api.post(`/orders/${orderId}/fees`, data),
    removeFee: (orderId, feeId, feeType) => api.delete(`/orders/${orderId}/fees/${feeId}`, { params: { fee_type: feeType } }),
};

// Products API
export const productsAPI = {
    getAll: (params) => api.get('/products', { params }),
    getOne: (id) => api.get(`/products/${id}`),
    create: (data) => api.post('/products', data),
    update: (id, data) => api.put(`/products/${id}`, data),
    delete: (id) => api.delete(`/products/${id}`),
    deleteAll: () => api.delete('/products/all'),
    getCategories: () => api.get('/products/categories'),
    importFromShopify: () => api.post('/products/import-shopify'),
    resetAndSyncShopify: () => api.post('/products/reset-sync-shopify'),
    importFromExcel: (file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/products/import-excel', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    uploadImages: (formData) => {
        return api.post('/products/upload-images', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
};

// Notification Settings API
export const notificationAPI = {
    getSettings: () => api.get('/notification-settings'),
    updateSettings: (data) => api.put('/notification-settings', data),
    getVendorsPriority: () => api.get('/vendors-priority'),
    updateVendorPriority: (vendorId, priority) => api.put(`/vendors-priority/${vendorId}`, { priority }),
    updateVendorsPriorityBulk: (priorities) => api.put('/vendors-priority-bulk', priorities),
    getQueue: () => api.get('/notification-queue'),
    processQueue: () => api.post('/notification-queue/process'),
    cancelEscalation: (orderId) => api.delete(`/notification-queue/${orderId}`),
    // Telegram
    getTelegramStatus: () => api.get('/telegram/status'),
    testTelegram: (chatId) => api.post('/telegram/test', { chat_id: chatId }),
};

export default api;
